import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { Loader } from 'ncoded-component-library';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { FormApi } from 'final-form';
import AddNewOption from 'components/AddNewOption';
import EditOption from 'components/EditOption';
import { isSingleElementArray } from 'utils';

import './QuestionSubjectsForm.styles.scss';

type QuestionSubjectsFormProps = {
  className?: string;
  afterEditSuccess?: (newName: string, oldName: string) => void;
  afterDeleteSuccess?: (name: string) => void;
};

const QuestionSubjectsForm: React.FC<QuestionSubjectsFormProps> = (props) => {
  const { className, afterEditSuccess, afterDeleteSuccess } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [editingValue, setEditingValue] = useState('');

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    questionSubjects,
    dataRoom: { _id },
    setDataRoom,
  } = useContext(DataRoomContext);

  const editableOptions = useMemo(
    () => questionSubjects.filter((value) => value !== 'Other'),
    [questionSubjects],
  );

  const showDelete = useMemo(
    () => !isSingleElementArray(editableOptions),
    [editableOptions],
  );

  const classes = classNames('question-subjects-form', className);

  const getInitialValues = useCallback(
    (option: string) => ({ name: option, oldName: option }),
    [],
  );

  const onAdd = useCallback(
    async ({ name }, formApi: FormApi) => {
      try {
        setIsLoading(true);
        const {
          data: { questionSubjects },
        } = await dataRoomApi.room.addQuestionSubject(_id, name);
        setDataRoom((prev) => ({
          ...prev,
          questionSubjects,
        }));
        formApi.reset();
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [_id, popServerError, setDataRoom, setIsLoading],
  );

  const onEdit = useCallback(
    async ({ name, oldName }) => {
      try {
        setIsLoading(true);
        const {
          data: { questionSubjects },
        } = await dataRoomApi.room.editQuestionSubject(_id, name, oldName);
        afterEditSuccess?.(name, oldName);
        setDataRoom((prev) => ({
          ...prev,
          questionSubjects,
        }));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [_id, afterEditSuccess, popServerError, setDataRoom],
  );

  const onDelete = useCallback(
    async (option) => {
      try {
        setIsLoading(true);
        const {
          data: { questionSubjects },
        } = await dataRoomApi.room.removeQuestionSubject(_id, option);
        afterDeleteSuccess?.(option);
        setDataRoom((prev) => ({
          ...prev,
          questionSubjects,
        }));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [_id, afterDeleteSuccess, popServerError, setDataRoom],
  );

  return (
    <div className={classes}>
      {isLoading && <Loader />}

      {editableOptions.map((option) => (
        <EditOption
          key={option}
          value={option}
          isEditing={editingValue === option}
          showDelete={showDelete}
          initialValues={getInitialValues(option)}
          onPencilClick={() => setEditingValue(option)}
          onCancel={() => setEditingValue('')}
          onDelete={() => onDelete(option)}
          onEdit={onEdit}
        />
      ))}

      <AddNewOption onAdd={onAdd} />
    </div>
  );
};

export default QuestionSubjectsForm;
